import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { readingTime } from "@/components/ArticleCard";

export default function ArticoloDettaglio() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((res) => setArticle(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const title = (lang === "en" && article?.title_en) || article?.title;
  const excerpt = (lang === "en" && article?.excerpt_en) || article?.excerpt;
  const content = (lang === "en" && article?.content_en) || article?.content;
  const paragraphs = (content || "").split("\n\n").filter(Boolean);

  useEffect(() => {
    if (!article) return;
    document.title = `${title} — Dott. Angilè`;
    const d = document.head.querySelector('meta[name="description"]');
    if (d && excerpt) d.setAttribute("content", excerpt);
  }, [article, title, excerpt]);
  const date = article?.published_at
    ? new Date(article.published_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <div data-testid="articolo-dettaglio-page" className="pt-24 sm:pt-32 min-h-screen">
      <div className="container-editorial max-w-3xl pb-20 sm:pb-28">
        <Link
          to="/articoli"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-brand link-underline"
        >
          <ArrowLeft className="h-4 w-4" /> {t.articles.eyebrow}
        </Link>

        {loading && <p className="mt-10 text-sm font-light text-slate-500">{t.articles.loading}</p>}

        {notFound && (
          <p className="mt-10 font-serif text-2xl text-slate-700">{t.articles.empty}</p>
        )}

        {article && (
          <article className="mt-8">
            <Reveal>
              <p className="eyebrow">{article.category}</p>
              <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-light leading-[1.12] text-slate-900 tracking-tight">
                {title}
              </h1>
              <p className="mt-5 text-base font-light italic leading-relaxed text-slate-500">{excerpt}</p>
              <div className="mt-6 flex items-center gap-4 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                <span>{date}</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>{readingTime(content)} min</span>
              </div>
            </Reveal>
            {article.cover_image && (
              <Reveal delay={0.1}>
                <div className="mt-10 overflow-hidden">
                  <img src={article.cover_image} alt={title} className="w-full h-72 sm:h-96 object-cover" />
                </div>
              </Reveal>
            )}
            <Reveal delay={0.15}>
              <div className="article-body mt-12">
                {paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </Reveal>
          </article>
        )}
      </div>
    </div>
  );
}
